import Config from "./config.js";

/**
 * Tracking — the dataLayer push on every submission. Runs FIRST in onFormSubmitted and owes
 * nothing to the caller's callbacks, so no callback can drop it.
 *
 * EVENT NAMES. A form with an `event` in config/hubspot/forms.php pushes that name
 * (`hsform_ba_contact_form` …) with the payload legacy's site-wide tracker sends
 * (legacy/src2/HSFormEvents/Tracker.js): GTM's Google Ads conversion triggers key off it, and
 * native pages do not load that tracker. Any other form pushes `hubspot_form_submitted`.
 *
 * NOT DOUBLE-COUNTED: where legacy's tracker IS on the page it pushes the hsform_* event itself
 * off HubSpot's postMessage, so this falls back to `hubspot_form_submitted`. GTM's own
 * `form_submission` (a Custom HTML tag on the same postMessage) is untouched and not pushed here.
 */
const Tracking = {};

/** `{ submissionValues }` (v2 onFormSubmitted) or `[{ name, value }]` → `{ name: value }`. */
Tracking.values = function (data) {
  const raw = data?.submissionValues ?? data ?? {};

  if (Array.isArray(raw)) return Object.fromEntries(raw.map((f) => [f.name, f.value]));

  return typeof raw === "object" ? raw : {};
};

/** A North American phone → E.164, "" when unrecognisable. Same rules as legacy's tracker. */
Tracking.e164 = function (raw) {
  if (typeof raw !== "string" || !raw.trim()) return "";

  const s = raw.trim();
  const digits = s.replace(/\D/g, "");

  if (s.startsWith("+")) return digits ? `+${digits}` : "";
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith("1")) return `+${digits}`;

  return "";
};

/**
 * @param  {{formId: string, name: string}} ctx
 * @param  {object} data  HubSpot's submission
 * @return {object} the dataLayer payload
 */
Tracking.payload = function (ctx, data) {
  const def = window.__thryvHSFormEventsInitialized ? null : Config.event(ctx.name);
  const values = Tracking.values(data);
  const payload = {
    event: def?.event || "hubspot_form_submitted",
    hubspot_form_id: ctx.formId,
    hubspot_form_name: def?.label || ctx.name || "",
    page_path: window.location?.pathname || "",
    site_country: Config.country(),
  };

  if (!def) return payload;

  const formData = {};

  (def.form_data || []).forEach((key) => {
    if (values[key] !== undefined && values[key] !== "") formData[key] = values[key];
  });

  if (Object.keys(formData).length) payload.form_data = formData;

  if (def.enhanced_conversions) {
    const ec = {};
    const email = values.email;
    const phone = Tracking.e164(values.phone);

    if (typeof email === "string" && email.includes("@")) ec.email = email.trim();
    if (phone) ec.phone_number = phone;
    if (Object.keys(ec).length) payload.enhanced_conversion_data = ec;
  }

  return payload;
};

/**
 * Push, then run `after` once GTM has processed the event — or after 2s if GTM is absent or slow,
 * so a redirect can never hang on it.
 */
Tracking.push = function (payload, after) {
  window.dataLayer = window.dataLayer || [];

  if (typeof after !== "function") {
    window.dataLayer.push(payload);

    return;
  }

  let done = false;
  const once = () => {
    if (!done) {
      done = true;
      after();
    }
  };

  window.dataLayer.push({ ...payload, eventCallback: once, eventTimeout: 2000 });
  setTimeout(once, 2000);
};

export default Tracking;
